interface ArticleAuthor {
  '@name'?: string;
  fullName?: string;
  photo?: { '@link'?: string };
  'jcr:uuid'?: string;
}

interface Article {
  '@name'?: string;
  '@id'?: string;
  title?: string;
  verdict?: string;
  authors?: ArticleAuthor[] | ArticleAuthor;
  photo?: { '@link'?: string };
  'mgnl:tags'?: string[] | string;
  'jcr:uuid'?: string;
}

// Magnolia /@nodes returns a flat Article[] array.
// jcrDeliveryEndpoint v2 (root) returns { results: [...], total } — we accept both.
type ArticlesResponse =
  | Article[]
  | { results?: Article[]; total?: number; _meta?: { total?: number } };

interface I18nBag {
  verdicts: Record<string, string>;
  defaultAuthor: string;
  filterTitle: string;
  filterAll: string;
  filterRemoveTag: string;
  paginationPrev: string;
  paginationNext: string;
  empty: string;
}

interface SectionConfig {
  sectionId: string;
  perPage: number;
  i18n: I18nBag;
}

const ESC_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

function esc(s: string | undefined | null): string {
  if (!s) return '';
  return String(s).replace(/[&<>"']/g, (c) => ESC_MAP[c] ?? c);
}

function readSectionConfig(section: HTMLElement): SectionConfig | null {
  const dataEl = section.querySelector<HTMLElement>('[data-fc-articles-config]');
  if (!dataEl?.textContent) return null;
  try {
    return JSON.parse(dataEl.textContent) as SectionConfig;
  } catch {
    return null;
  }
}

function articlesFrom(data: ArticlesResponse): Article[] {
  if (Array.isArray(data)) return data;
  return data.results ?? [];
}

function totalFrom(
  data: ArticlesResponse,
  currentLength: number,
  offset: number,
  limit: number,
): number {
  if (!Array.isArray(data)) {
    if (typeof data.total === 'number') return data.total;
    if (typeof data._meta?.total === 'number') return data._meta.total;
  }
  return currentLength < limit ? offset + currentLength : offset + currentLength + 1;
}

function paginationWindow(current: number, total: number, delta = 2): (number | '…')[] {
  const range: number[] = [];
  for (let p = 1; p <= total; p++) {
    if (p === 1 || p === total || (p >= current - delta && p <= current + delta)) {
      range.push(p);
    }
  }
  const items: (number | '…')[] = [];
  let prev = 0;
  for (const p of range) {
    if (prev > 0 && p - prev > 1) items.push('…');
    items.push(p);
    prev = p;
  }
  return items;
}

function renderCard(article?: Article, cfg?: SectionConfig): string {
  if (!article) return '';

  const photoUrl = article?.photo?.['@link'] ?? '';
  if (!photoUrl) return '';

  const title = article?.title ?? '';
  const href = article?.['jcr:uuid'] ?? '';

  const authorsArr = Array.isArray(article.authors)
    ? article.authors
    : article.authors
      ? [article.authors]
      : [];
  const author = authorsArr?.[0];
  const authorName = author?.fullName?.trim() ?? cfg?.i18n?.defaultAuthor;
  const authorPhoto = author?.photo?.['@link'] ?? '';
  const authorHref = author?.['jcr:uuid'] ?? '';

  const verdictLabel = article.verdict ? cfg?.i18n?.verdicts?.[article?.verdict] : '';
  const verdictBlock = verdictLabel
    ? `<span class="fc-verdict-pill fc-verdict-pill--${esc(article.verdict)} fc-article-card__verdict" aria-hidden="true">${esc(verdictLabel)}</span>`
    : '';

  const tags = Array.isArray(article['mgnl:tags'])
    ? article['mgnl:tags']
    : article['mgnl:tags']
      ? [article['mgnl:tags']]
      : [];

  const categoryBlock = tags?.length
    ? `<div class="fc-article-card__category-wrapper">
      ${tags
        .slice(0, 3)
        .map((tag) => `<span class="fc-tag">${esc(tag)}</span>`)
        .join('')}
     </div>`
    : '';

  const avatarBlock = authorPhoto
    ? `<span class="fc-avatar fc-article-card__author-avatar" aria-hidden="true"><img class="fc-avatar__img" src="${esc(authorPhoto)}" alt="" loading="lazy" /></span>`
    : `<span class="fc-avatar fc-article-card__author-avatar" aria-hidden="true"></span>`;

  const authorBlock = authorHref
    ? `<a class="fc-article-card__author" href="${esc(authorHref)}">${avatarBlock}<span class="fc-article-card__author-name">${esc(authorName)}</span></a>`
    : `<div class="fc-article-card__author">${avatarBlock}<span class="fc-article-card__author-name">${esc(authorName)}</span></div>`;

  const titleBlock = title
    ? href
      ? `<h3 class="fc-article-card__title"><a class="fc-article-card__title-link" href="${esc(href)}">${esc(title)}</a></h3>`
      : `<h3 class="fc-article-card__title">${esc(title)}</h3>`
    : '';

  return `<li class="cell">
  <article class="fc-article-card fc-article-card--list">
    <div class="fc-article-card__media">
      <img class="fc-article-card__image" src="${esc(photoUrl)}" alt="" loading="lazy" />
    </div>
    <div class="fc-article-card__content">
      ${categoryBlock}
      ${titleBlock}
      ${verdictBlock}
      ${authorBlock}
    </div>
  </article>
</li>`;
}

function renderPagination(
  cfg: SectionConfig,
  currentPage: number,
  totalPages: number,
  buildUrl: (page: number) => { restUrl: string; pageUrl: string },
  activeTags: string[],
): string {
  if (totalPages <= 1) return '';

  const tagsAttr = esc(activeTags.join(','));
  const items: string[] = [];
  if (currentPage > 1) {
    const { restUrl, pageUrl } = buildUrl(currentPage - 1);
    items.push(
      `
        <li>
          <a
            class="fc-article-list__pagination-link"
            hx-get="${esc(restUrl)}"
            hx-target="#${esc(cfg.sectionId)}-grid"
            hx-swap="innerHTML show:#${esc(cfg.sectionId)}:top"
            hx-push-url="${esc(pageUrl)}"
            data-fc-tag="${tagsAttr}"
            data-fc-page="${currentPage - 1}"
            href="${esc(pageUrl)}"
            rel="prev"
            aria-label="${esc(cfg.i18n.paginationPrev)}"
          >
            ‹
          </a>
        </li>`,
    );
  }
  for (const p of paginationWindow(currentPage, totalPages)) {
    if (p === '…') {
      items.push(
        `<li><span class="fc-article-list__pagination-ellipsis" aria-hidden="true">…</span></li>`,
      );
    } else if (p === currentPage) {
      items.push(
        `<li><span class="fc-article-list__pagination-link fc-article-list__pagination-link--active" aria-current="page">${p}</span></li>`,
      );
    } else {
      const { restUrl, pageUrl } = buildUrl(p);
      items.push(
        `<li>
          <a
            class="fc-article-list__pagination-link"
            hx-get="${esc(restUrl)}"
            hx-target="#${esc(cfg.sectionId)}-grid"
            hx-swap="innerHTML show:#${esc(cfg.sectionId)}:top"
            hx-push-url="${esc(pageUrl)}"
            data-fc-tag="${tagsAttr}"
            data-fc-page="${p}"
            href="${esc(pageUrl)}"
          >
            ${p}
          </a>
        </li>
        `,
      );
    }
  }
  if (currentPage < totalPages) {
    const { restUrl, pageUrl } = buildUrl(currentPage + 1);
    items.push(
      `
        <li>
          <a
            class="fc-article-list__pagination-link"
            hx-get="${esc(restUrl)}"
            hx-target="#${esc(cfg.sectionId)}-grid"
            hx-swap="innerHTML show:#${esc(cfg.sectionId)}:top"
            hx-push-url="${esc(pageUrl)}"
            data-fc-tag="${tagsAttr}"
            data-fc-page="${currentPage + 1}"
            href="${esc(pageUrl)}"
            rel="next"
            aria-label="${esc(cfg.i18n.paginationNext)}"
          >
            ›
          </a>
        </li>`,
    );
  }
  return `<ul class="fc-article-list__pagination-list">${items.join('')}</ul>`;
}

function findSection(target: Element | null): HTMLElement | null {
  if (!target) return null;
  return target.closest<HTMLElement>('[data-fc-article-list]');
}

function parseHxUrlParams(restUrl: string): {
  offset: number;
  tag: string;
  limit: number;
  ancestor: string;
} {
  const u = new URL(restUrl, window.location.origin);
  const offset = parseInt(u.searchParams.get('offset') ?? '0', 10);
  const limit = parseInt(u.searchParams.get('limit') ?? '9', 10);
  const tag = u.searchParams.get('mgnl:tags') ?? '';
  const ancestor = u.searchParams.get('@ancestor') ?? '';
  return { offset, limit, tag, ancestor };
}

// --- Multi-tag helpers ---

function getActiveTags(section: HTMLElement): string[] {
  const raw = section.dataset.fcActiveTags ?? '';
  return raw ? raw.split(',').filter(Boolean) : [];
}

function setActiveTags(section: HTMLElement, tags: string[]): void {
  section.dataset.fcActiveTags = tags.join(',');
}

function renderActiveTagChips(section: HTMLElement, tags: string[], cfg: SectionConfig): void {
  const container = section.querySelector<HTMLElement>('[data-fc-active-tags-container]');
  if (!container) return;
  if (tags.length === 0) {
    container.innerHTML = '';
    container.hidden = true;
    return;
  }
  container.hidden = false;
  container.innerHTML = tags
    .map(
      (tag) =>
        `<span class="fc-tags-list__active-tag">` +
        `<span class="fc-tags-list__active-tag-label">${esc(tag.replace(/-/g, ' '))}</span>` +
        `<button class="fc-tags-list__active-tag-remove" data-fc-remove-tag="${esc(tag)}" type="button" aria-label="${esc(cfg.i18n.filterRemoveTag)}: ${esc(tag.replace(/-/g, ' '))}">×</button>` +
        `</span>`,
    )
    .join('');
}

function updateSidebarState(section: HTMLElement, tags: string[]): void {
  const sidebar = section.querySelector<HTMLElement>('.fc-article-list__sidebar');
  if (!sidebar) return;

  let firstSelectedEl: HTMLAnchorElement | null = null;
  sidebar.querySelectorAll<HTMLAnchorElement>('.fc-tags-list__tag').forEach((a) => {
    const tagVal = a.dataset.fcTag ?? '';
    if (!tagVal) {
      // "All" button
      const isAll = tags.length === 0;
      a.classList.toggle('fc-tags-list__tag--inactive', !isAll);
      if (isAll) a.setAttribute('aria-current', 'true');
      else a.removeAttribute('aria-current');
      return;
    }
    const isSelected = tags.includes(tagVal);
    const isDisabledByMax = !isSelected && tags.length >= 3;
    const isInactive = tags.length > 0 && !isSelected;
    a.classList.toggle('fc-tags-list__tag--selected', isSelected);
    a.classList.toggle('fc-tags-list__tag--inactive', isInactive);
    a.classList.toggle('fc-tags-list__tag--disabled', isDisabledByMax);
    if (isSelected) {
      a.setAttribute('aria-current', 'true');
      if (!firstSelectedEl) firstSelectedEl = a;
    } else {
      a.removeAttribute('aria-current');
    }
    if (isDisabledByMax) a.setAttribute('aria-disabled', 'true');
    else a.removeAttribute('aria-disabled');
  });

  if (firstSelectedEl) {
    const targetEl = firstSelectedEl;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        (targetEl as HTMLAnchorElement).scrollIntoView({ block: 'nearest' });
      });
    });
  }
}

function triggerGridRefresh(
  section: HTMLElement,
  cfg: SectionConfig,
  tags: string[],
  page: number,
): void {
  const grid = section.querySelector<HTMLElement>('[data-fc-articles-grid]');
  if (!grid) return;

  const gridHxGet = grid.getAttribute('hx-get') ?? '';
  const restBase = gridHxGet.split('?')[0];
  const { ancestor } = parseHxUrlParams(gridHxGet);

  const isMulti = tags.length > 1;
  const reqParams = new URLSearchParams();
  if (ancestor) reqParams.set('@ancestor', ancestor);
  reqParams.set('limit', isMulti ? '999' : String(cfg.perPage));
  reqParams.set('offset', isMulti ? '0' : String((page - 1) * cfg.perPage));
  reqParams.set('orderBy', 'publicationDate desc');
  if (tags.length > 0) reqParams.set('mgnl:tags', tags[0]);

  const restUrl = `${restBase}?${reqParams.toString()}`;

  // Store state on grid for the beforeSwap handler
  grid.dataset.fcActiveTags = tags.join(',');
  grid.dataset.fcPage = String(page);

  // Update browser URL
  const pageUrl = new URL(window.location.href);
  const tagKey =
    Array.from(pageUrl.searchParams.keys()).find((k) => k.startsWith('tag-')) ??
    `tag-${cfg.sectionId.replace('fc-article-list-', '').substring(0, 8)}`;
  const pageKey = Array.from(pageUrl.searchParams.keys()).find((k) => k.startsWith('page-'));
  if (tags.length > 0) {
    pageUrl.searchParams.set(tagKey, tags.join(','));
  } else {
    pageUrl.searchParams.delete(tagKey);
  }
  if (pageKey) pageUrl.searchParams.delete(pageKey);
  history.pushState({}, '', pageUrl.pathname + (pageUrl.search || '') + '#' + cfg.sectionId);

  // @ts-expect-error - htmx is global
  window.htmx?.ajax('GET', restUrl, {
    target: grid,
    swap: `innerHTML show:#${cfg.sectionId}:top`,
  });
}

export function initArticleList(): void {
  // Unified click handler — capture phase so it fires before htmx
  document.body.addEventListener(
    'click',
    (e: MouseEvent) => {
      const target = e.target as Element;

      // Remove-tag chip button
      const removeBtn = target.closest<HTMLButtonElement>('[data-fc-remove-tag]');
      if (removeBtn) {
        const section = findSection(removeBtn);
        if (!section) return;
        const cfg = readSectionConfig(section);
        if (!cfg) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        const tagToRemove = removeBtn.dataset.fcRemoveTag ?? '';
        const next = getActiveTags(section).filter((t) => t !== tagToRemove);
        setActiveTags(section, next);
        renderActiveTagChips(section, next, cfg);
        updateSidebarState(section, next);
        triggerGridRefresh(section, cfg, next, 1);
        return;
      }

      // Sidebar tag link
      const tagLink = target.closest<HTMLAnchorElement>('.fc-tags-list__tag');
      if (tagLink) {
        const section = findSection(tagLink);
        if (!section) return;
        const cfg = readSectionConfig(section);
        if (!cfg) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        const clickedTag = tagLink.dataset.fcTag ?? '';
        const current = getActiveTags(section);
        let next: string[];
        if (!clickedTag) {
          // "All" clicked
          next = [];
        } else if (current.includes(clickedTag)) {
          // Toggle off
          next = current.filter((t) => t !== clickedTag);
        } else if (current.length >= 3) {
          // Max 3 tags reached
          return;
        } else {
          next = [...current, clickedTag];
        }
        setActiveTags(section, next);
        renderActiveTagChips(section, next, cfg);
        updateSidebarState(section, next);
        triggerGridRefresh(section, cfg, next, 1);
        return;
      }

      // Pagination link
      const paginationLink = target.closest<HTMLAnchorElement>('.fc-article-list__pagination-link');
      if (
        paginationLink &&
        !paginationLink.classList.contains('fc-article-list__pagination-link--active')
      ) {
        const section = findSection(paginationLink);
        if (!section) return;
        const cfg = readSectionConfig(section);
        if (!cfg) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        const page = parseInt(paginationLink.dataset.fcPage ?? '1', 10);
        const tags = getActiveTags(section);
        triggerGridRefresh(section, cfg, tags, page);
        return;
      }
    },
    true,
  );

  document.body.addEventListener('htmx:beforeSwap', (evt: Event) => {
    const ce = evt as unknown as {
      detail: {
        target: Element;
        xhr: XMLHttpRequest;
        serverResponse: string;
        requestConfig: {
          path: string;
          elt?: HTMLElement;
        };
      };
    };
    const grid = ce.detail.target as HTMLElement;
    if (!grid?.matches?.('[data-fc-articles-grid]')) return;

    const section = findSection(grid);
    if (!section) return;
    const cfg = readSectionConfig(section);
    if (!cfg) return;

    let data: ArticlesResponse;
    try {
      data = JSON.parse(ce.detail.xhr.response) as ArticlesResponse;
    } catch {
      return;
    }

    // Read active tags from grid element (set by triggerGridRefresh or FTL initial state)
    const activeTagsRaw = grid.dataset.fcActiveTags ?? '';
    const activeTags = activeTagsRaw ? activeTagsRaw.split(',').filter(Boolean) : [];
    const isMultiTag = activeTags.length > 1;

    const {
      offset,
      tag: primaryTag,
      limit,
      ancestor,
    } = parseHxUrlParams(ce.detail.requestConfig.path);

    // Fallback for initial page load if grid attr not yet set
    if (!activeTagsRaw && primaryTag) {
      activeTags.push(primaryTag);
      grid.dataset.fcActiveTags = primaryTag;
    }

    // Server returned articles pre-filtered by primaryTag; post-filter for additional tags (AND)
    let allItems = articlesFrom(data);
    for (const t of activeTags.slice(1)) {
      allItems = allItems.filter((a) => {
        const articleTags = Array.isArray(a['mgnl:tags'])
          ? a['mgnl:tags']
          : a['mgnl:tags']
            ? [a['mgnl:tags']]
            : [];
        return articleTags.includes(t);
      });
    }

    // Pagination
    let currentPage: number;
    let pageItems: Article[];
    let total: number;

    if (isMultiTag) {
      // Client-side pagination over the fully post-filtered result set
      currentPage = parseInt(grid.dataset.fcPage ?? '1', 10);
      const clientOffset = (currentPage - 1) * cfg.perPage;
      pageItems = allItems.slice(clientOffset, clientOffset + cfg.perPage);
      total = allItems.length;
    } else {
      currentPage = Math.floor(offset / cfg.perPage) + 1;
      pageItems = allItems;
      total = totalFrom(data, allItems.length, offset, limit);
    }

    const totalPages = Math.max(1, Math.ceil(total / cfg.perPage));

    const cards = pageItems
      .map((a) => renderCard(a, cfg))
      .filter((s) => s.length > 0)
      .join('\n');
    ce.detail.serverResponse =
      cards.length > 0 ? cards : `<p class="fc-article-list__empty">${esc(cfg.i18n.empty)}</p>`;

    const paginationNav = section.querySelector<HTMLElement>('.fc-article-list__pagination');
    if (paginationNav) {
      const restBase = ce.detail.requestConfig.path.split('?')[0];
      const buildUrl = (page: number): { restUrl: string; pageUrl: string } => {
        const params = new URLSearchParams();
        if (ancestor) params.set('@ancestor', ancestor);
        if (isMultiTag) {
          params.set('limit', '999');
          params.set('offset', '0');
        } else {
          params.set('limit', String(cfg.perPage));
          params.set('offset', String((page - 1) * cfg.perPage));
        }
        params.set('orderBy', 'publicationDate desc');
        if (activeTags.length > 0) params.set('mgnl:tags', activeTags[0]);
        const rUrl = `${restBase}?${params.toString()}`;

        const pageUrlObj = new URL(window.location.href);
        const tagKey =
          Array.from(pageUrlObj.searchParams.keys()).find((k) => k.startsWith('tag-')) ??
          `tag-${cfg.sectionId.replace('fc-article-list-', '').substring(0, 8)}`;
        const pageKey =
          Array.from(pageUrlObj.searchParams.keys()).find((k) => k.startsWith('page-')) ??
          `page-${cfg.sectionId.replace('fc-article-list-', '').substring(0, 8)}`;

        if (activeTags.length > 0) {
          pageUrlObj.searchParams.set(tagKey, activeTags.join(','));
        } else {
          pageUrlObj.searchParams.delete(tagKey);
        }
        if (page === 1) pageUrlObj.searchParams.delete(pageKey);
        else pageUrlObj.searchParams.set(pageKey, String(page));

        return {
          restUrl: rUrl,
          pageUrl: pageUrlObj.pathname + (pageUrlObj.search || '') + '#' + cfg.sectionId,
        };
      };

      paginationNav.innerHTML = renderPagination(
        cfg,
        currentPage,
        totalPages,
        buildUrl,
        activeTags,
      );
      // @ts-expect-error - htmx is global
      window.htmx?.process(paginationNav);
    }

    // Sync sidebar and chips after swap
    updateSidebarState(section, activeTags);
    renderActiveTagChips(section, activeTags, cfg);
  });
}
